import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/nextjs';

type FeatureCollection = {
  bbox: number[];
  features: {
    bbox?: number[];
    geometry: {
      coordinates: number[];
      type: string;
    };
    properties: {
      accuracy: string;
      addendum: {
        geonames?: {
          feature_code: string;
        };
        concordances?: {
          [key: string]: any; // Dynamic key-value pairs
        };
      };
      continent: string;
      continent_gid: string;
      country: string;
      country_a: string;
      country_code: string;
      country_gid: string;
      gid: string;
      id: string;
      label: string;
      layer: string;
      name: string;
      region?: string;
      region_a?: string;
      region_gid?: string;
      source: string;
      source_id: string;
    };
    type: string;
  }[];
  geocoding: {
    attribution: string;
    engine: {
      author: string;
      name: string;
      version: string;
    };
    query: {
      lang: {
        defaulted: boolean;
        iso6391: string;
        iso6393: string;
        name: string;
        via: string;
      };
      layers: string[];
      parsed_text: {
        subject: string;
      };
      parser: string;
      private: boolean;
      querySize: number;
      size: number;
      text: string;
    };
    timestamp: number;
    version: string;
  };
  type: string;
};

export type LocationType = {
  id: string;
  label: string;
  lat: number;
  lng: number;
};

export async function searchLocation(search: string, token: string, abortSignal?: AbortSignal) {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_API_DEVELOPMENT_ENDPOINT}/location-provider?query=${search}`,
    {
      signal: abortSignal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const data: FeatureCollection = await response.json();

  return data.features.map((feature) => {
    return {
      id: feature.properties.id,
      label: feature.properties.label,
      lat: feature.geometry.coordinates[1],
      lng: feature.geometry.coordinates[0],
    };
  });
}

export default function useSearchLocation(search: string) {
  const { getToken, isSignedIn } = useAuth();
  return useQuery({
    queryKey: ['searchLocation', search],
    enabled: isSignedIn && search.length > 0,
    queryFn: async ({ signal }) => {
      const token = await getToken();
      return searchLocation(search, token ?? '', signal);
    },
  });
}
